import { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { Clock, TrendingUp, Calendar, Sparkles, Check, ChevronDown, ChevronLeft, ChevronRight, MapPin, Mail, Instagram, Facebook, Youtube, ArrowRight, X, Loader2, DollarSign, Users, ShoppingBag, RefreshCw, Leaf, Dumbbell, Zap, Flame, Wind, Eye, Shield, Target, Crosshair, Heart, Waves, MessageCircle, Send, Baby, Building2, User, PartyPopper, Cake, Gift, Crown, Trophy, Martini } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Package definitions for UI
const PACKAGE_OPTIONS = {
  opening: [
    { id: "opening_2weeks", name: "2 Weeks Unlimited", price: "$55" },
    { id: "opening_1month", name: "1 Month Unlimited", price: "$100" },
    { id: "opening_3months", name: "3 Months Unlimited", price: "$300" },
  ],
  presale: [
    { id: "presale_6months_weekly", name: "6 Months Foundation (Weekly)", price: "$45/week" },
    { id: "presale_6months_monthly", name: "6 Months Foundation (Monthly)", price: "$195/month" },
    { id: "presale_6months_annual", name: "6 Months Foundation (Pay Upfront)", price: "$1,170" },
    { id: "presale_12months_weekly", name: "12 Months Foundation (Weekly)", price: "$39/week" },
    { id: "presale_12months_monthly", name: "12 Months Foundation (Monthly)", price: "$169/month" },
    { id: "presale_12months_annual", name: "12 Months Foundation (Pay Upfront)", price: "$2,028" },
  ],
  presale_student_senior: [
    { id: "presale_student_senior_weekly", name: "12 Months Student & Senior (Weekly)", price: "$35/week" },
    { id: "presale_student_senior_monthly", name: "12 Months Student & Senior (Monthly)", price: "$152/month" },
    { id: "presale_student_senior_annual", name: "12 Months Student & Senior (Pay Upfront)", price: "$1,820" },
  ],
  other: [
    { id: "trial_offer", name: "Trial Offer (6 Classes)", price: "$55" },
    { id: "casual_class", name: "Casual Class (Drop-in)", price: "$35" },
    { id: "weekly_2classes", name: "2x Classes Weekly", price: "$49/week" },
    { id: "weekly_3classes", name: "3x Classes Weekly", price: "$59/week" },
    { id: "class_pack_10", name: "10 Class Pack", price: "$300" },
    { id: "private_package", name: "Private Package (5 Sessions)", price: "$450" },
  ],
};

// Booking Modal Context
const BookingContext = createContext();

const useBooking = () => useContext(BookingContext);

// MindBody URLs
const MINDBODY_PURCHASE_URL = "https://clients.mindbodyonline.com/classic/ws?studioid=5751621&stype=41";
const MINDBODY_SHOP_URL = "https://clients.mindbodyonline.com/classic/ws?studioid=5751621&stype=40&prodId=107";

// Stripe checkout function (backup if MindBody is down)
const initiateStripeCheckout = async (packageId) => {
  try {
    const originUrl = window.location.origin;
    const response = await fetch(`${BACKEND_URL}/api/checkout/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        package_id: packageId || 'general_purchase',
        origin_url: originUrl
      })
    });

    if (!response.ok) {
      throw new Error('Failed to create checkout session');
    }

    const data = await response.json();
    
    if (data.url) {
      window.location.href = data.url;
    } else {
      throw new Error('No checkout URL received');
    }
  } catch (error) {
    console.error('Stripe checkout error:', error);
  }
};

// Smart redirect function - tries MindBody, falls back to Stripe
const openWithFallback = (mindbodyUrl, stripePackageId = 'general_purchase') => {
  // Try to open MindBody
  const newWindow = window.open(mindbodyUrl, '_blank');
  
  // Check if popup was blocked or failed
  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    initiateStripeCheckout(stripePackageId);
    return;
  }
  
  // Set up a check to see if MindBody loaded (best effort)
  // If the window is still about:blank after 5 seconds, offer Stripe fallback
  setTimeout(() => {
    try {
      // If we can't access the URL (cross-origin), MindBody likely loaded fine
      // This is just a safety net for edge cases
    } catch (e) {
      // Cross-origin error is expected when MindBody loaded successfully
      console.warn('Cross-origin check (expected):', e.message);
    }
  }, 5000);
};

// Function to open MindBody purchase page with Stripe fallback
const openMindBodyPurchase = () => {
  openWithFallback(MINDBODY_PURCHASE_URL, 'opening_1month');
};

// Function to open MindBody membership shop with Stripe fallback
const openMindBodyMembership = () => {
  openWithFallback(MINDBODY_SHOP_URL, 'presale_6months_weekly');
};

// Booking Modal Component with Package Selection
const BookingModal = ({ isOpen, onClose, initialCategory }) => {
  const [selectedCategory, setSelectedCategory] = useState(initialCategory || 'opening');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (initialCategory) {
        setSelectedCategory(initialCategory);
      }
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialCategory]);

  const handlePackageSelect = async (packageId) => {
    // Redirect to MindBody for all package purchases
    openMindBodyPurchase();
    onClose();
  };

  if (!isOpen) return null;

  const categories = [
    { id: 'opening', label: 'Opening Specials' },
    { id: 'presale', label: 'Foundation Memberships' },
    { id: 'presale_student_senior', label: 'Student & Senior' },
    { id: 'other', label: 'Other Packages' },
  ];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-sm">
      <div className="relative bg-[#1a1a1a] border border-[#C97654] rounded-2xl p-6 md:p-8 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-[#E8DCC4]/60 hover:text-[#C97654] transition-colors z-10"
        >
          <X className="w-6 h-6" />
        </button>
        
        <div className="text-center mb-6">
          <Sparkles className="w-10 h-10 text-[#C97654] mx-auto mb-3" />
          <h2 className="text-2xl font-semibold text-[#E8DCC4] mb-2">Purchase Packages</h2>
          <p className="text-[#E8DCC4]/70 text-sm">Select a package to purchase via MindBody</p>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                selectedCategory === cat.id
                  ? 'bg-[#C97654] text-white'
                  : 'bg-[#2a2a2a] text-[#E8DCC4]/70 hover:bg-[#3a3a3a]'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {/* Package Options */}
        <div className="space-y-3">
          {PACKAGE_OPTIONS[selectedCategory]?.map((pkg) => {
            // Determine payment type and colors
            const isWeekly = pkg.id.includes('weekly') || pkg.name.toLowerCase().includes('weekly');
            const isMonthly = pkg.id.includes('monthly') || pkg.name.toLowerCase().includes('monthly');
            const isAnnual = pkg.id.includes('annual') || pkg.name.toLowerCase().includes('upfront');
            
            let borderColor = 'border-[#3a3a3a]';
            let hoverBorder = 'hover:border-[#C97654]';
            let bgColor = 'bg-[#2a2a2a]';
            let priceColor = 'text-[#C97654]';
            
            if (isWeekly) {
              borderColor = 'border-[#FF6B35]';
              hoverBorder = 'hover:border-[#FF8C5A]';
              bgColor = 'bg-[#FF6B35]/10';
              priceColor = 'text-[#FF6B35]';
            } else if (isMonthly) {
              borderColor = 'border-[#FF1493]';
              hoverBorder = 'hover:border-[#FF69B4]';
              bgColor = 'bg-[#FF1493]/10';
              priceColor = 'text-[#FF1493]';
            } else if (isAnnual) {
              borderColor = 'border-[#8B2635]';
              hoverBorder = 'hover:border-[#a03042]';
              bgColor = 'bg-[#8B2635]/10';
              priceColor = 'text-[#8B2635]';
            }
            
            // Format name with highlighted payment frequency
            const formatName = (name) => {
              if (name.toLowerCase().includes('weekly')) {
                return name.replace(/\(Weekly\)/i, '').trim();
              } else if (name.toLowerCase().includes('monthly')) {
                return name.replace(/\(Monthly\)/i, '').trim();
              } else if (name.toLowerCase().includes('upfront')) {
                return name.replace(/\(Pay Upfront\)/i, '').trim();
              }
              return name;
            };
            
            const getFrequencyBadge = () => {
              if (isWeekly) {
                return <span className="px-2 py-1 bg-[#FF6B35] text-white text-xs rounded-full font-bold">WEEKLY</span>;
              } else if (isMonthly) {
                return <span className="px-2 py-1 bg-[#FF1493] text-white text-xs rounded-full font-bold">MONTHLY</span>;
              } else if (isAnnual) {
                return <span className="px-2 py-1 bg-[#8B2635] text-white text-xs rounded-full font-bold">PAY UPFRONT</span>;
              }
              return null;
            };
            
            return (
              <button
                key={pkg.id}
                onClick={() => handlePackageSelect(pkg.id)}
                disabled={loading}
                className={`w-full flex items-center justify-between p-4 ${bgColor} border-2 ${borderColor} rounded-xl ${hoverBorder} hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div className="text-left flex items-center gap-3">
                  <p className="text-[#E8DCC4] font-medium">{formatName(pkg.name)}</p>
                  {getFrequencyBadge()}
                </div>
                <div className="flex items-center gap-3">
                  <span className={`${priceColor} font-bold text-lg`}>{pkg.price}</span>
                  {loading ? (
                    <Loader2 className={`w-5 h-5 ${priceColor} animate-spin`} />
                  ) : (
                    <ArrowRight className="w-5 h-5 text-[#E8DCC4]/60" />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <p className="text-[#E8DCC4]/50 text-xs text-center mt-6">
          Secure payment powered by Stripe. You'll receive a confirmation email after purchase.
        </p>
      </div>
    </div>
  );
};

// MindBody Booking - opens in new tab for best compatibility
const openMindBodyBooking = () => {
  window.open('https://go.mindbodyonline.com/book/widgets/schedules/view/24485199377/schedule', '_blank');
};

// Opens the MindBody schedule on the next Tuesday or Thursday
// (Mums & Bubs classes run on Tue/Thu at this studio).
const openMumsBubsBooking = () => {
  const now = new Date();
  const day = now.getDay(); // 0=Sun … 2=Tue … 4=Thu
  const toTue = (2 - day + 7) % 7; // 0 if today is Tuesday
  const toThu = (4 - day + 7) % 7; // 0 if today is Thursday
  const daysUntil = Math.min(toTue, toThu);
  const target = new Date(now);
  target.setDate(now.getDate() + daysUntil);
  const yyyy = target.getFullYear();
  const mm = String(target.getMonth() + 1).padStart(2, '0');
  const dd = String(target.getDate()).padStart(2, '0');
  window.open(`https://go.mindbodyonline.com/book/widgets/schedules/view/24485199377/schedule?date=${yyyy}-${mm}-${dd}`, '_blank');
};

// Booking Provider Component
const BookingProvider = ({ children }) => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [bookingCategory, setBookingCategory] = useState('opening');
  
  const openBooking = (category = 'opening') => {
    setBookingCategory(category);
    setIsBookingOpen(true);
  };
  const closeBooking = () => setIsBookingOpen(false);
  
  // Use the direct MindBody link for best compatibility
  const openMindBody = openMindBodyBooking;
  
  return (
    <BookingContext.Provider value={{ openBooking, closeBooking, openMindBody }}>
      {children}
      <BookingModal isOpen={isBookingOpen} onClose={closeBooking} initialCategory={bookingCategory} />
    </BookingContext.Provider>
  );
};


// Custom Images from the original site
const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_170075d8-a168-40c8-9457-8f5d6f2e3d05/artifacts/wi6dq9kq_3636c9c1-c71a-4f32-8d41-ab8edb94b6f2.jpeg",
  about: "https://customer-assets.emergentagent.com/job_passion-flow/artifacts/dz2shqen_96639D59-41EA-4FCE-A89C-7386688511FD.png",
  classes: "https://customer-assets.emergentagent.com/job_passion-preview/artifacts/xhtu2k7r_F37FAE59-6529-472D-A1E9-0734BE031462.png"
};

// Animated Image Component - fades in when visible
const AnimatedImage = ({ src, alt, className, overlayClassName }) => {
  const [isVisible, setIsVisible] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className="relative overflow-hidden">
      <img 
        src={src} 
        alt={alt} 
        className={`${className} transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      />
      {overlayClassName && <div className={overlayClassName}></div>}
    </div>
  );
};

// Pilates Logo Component (using image)
const PilatesLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_passion-preview/artifacts/ehva27k0_Copy%20of%20Copy%20of%20Copy%20of%20Copy%20of%20Copy%20of%20Black%20and%20White%20Circle%20PILATES%20Logo%20%28100%20x%20500%20px%29%20%28500%20x%20800%20px%29.jpeg" 
    alt="Passion Pilates Logo" 
    className={className + " object-contain"}
  />
);

// Navigation Component
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { openBooking, openMindBody } = useBooking();
  
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <PilatesLogo className="w-11 h-11" />
          <span className="text-white font-light text-xl tracking-[0.25em]">PASSION PILATES<span className="text-[0.4rem] ml-2 align-super relative -top-1 tracking-tight">AU</span></span>
        </div>
        
        {/* Desktop Menu */}
        <div className="hidden md:flex items-center gap-8">
          {['ABOUT', 'CLASSES', 'SCHEDULE', 'SPECIAL OFFERS', 'FAQ', 'CONTACT'].map((item) => (
            <button 
              key={item}
              onClick={() => scrollToSection(item === 'SPECIAL OFFERS' ? 'opening-specials' : item.toLowerCase())}
              className="text-[#E8DCC4]/80 hover:text-[#C97654] transition-colors text-sm tracking-wider font-medium"
            >
              {item}
            </button>
          ))}
          <a
            href="/blog"
            className="text-[#E8DCC4]/80 hover:text-[#C97654] transition-colors text-sm tracking-wider font-medium"
            data-testid="nav-journal-link"
          >
            JOURNAL
          </a>
          <button 
            onClick={openMindBody}
            className="border border-[#C97654] text-[#C97654] px-5 py-2 rounded-md text-sm hover:bg-[#C97654]/10 transition-all duration-300 font-medium"
          >
            Book Class
          </button>
          <button 
            onClick={openMindBodyMembership}
            className="bg-[#B85C38] text-white px-6 py-2 rounded-md text-sm hover:bg-[#C97654] transition-all duration-300 font-medium"
          >
            Buy Now
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-[#E8DCC4]"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-black border-t border-[#2a2a2a] px-6 py-4">
          {[
            { label: 'ABOUT',          color: '#FBF6EC' }, // off-white
            { label: 'CLASSES',        color: '#E8804C' }, // burnt orange
            { label: 'SCHEDULE',       color: '#C9A46A' }, // warm gold
            { label: 'SPECIAL OFFERS', color: '#B85C38' }, // rust
            { label: 'FAQ',            color: '#9A5B70' }, // plum
            { label: 'CONTACT',        color: '#4A6572' }, // slate blue
          ].map((item) => (
            <button
              key={item.label}
              onClick={() => scrollToSection(item.label === 'SPECIAL OFFERS' ? 'opening-specials' : item.label.toLowerCase())}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="block w-full text-left py-3 text-sm tracking-[0.15em] font-bold uppercase transition-opacity hover:opacity-70"
              style={{ color: item.color }}
            >
              {item.label}
            </button>
          ))}
          <a
            href="/blog"
            data-testid="mobile-nav-journal"
            className="block w-full text-left py-3 text-sm tracking-[0.15em] font-bold uppercase transition-opacity hover:opacity-70"
            style={{ color: '#7C8B5C' }}
          >
            JOURNAL
          </a>
          <button
            onClick={() => { openMindBody(); setMobileMenuOpen(false); }}
            data-testid="mobile-nav-book-class"
            className="block w-full text-left py-3 text-[15px] tracking-[0.18em] uppercase font-black transition-opacity hover:opacity-80"
            style={{ color: '#FFFFFF' }}
          >
            BOOK CLASS →
          </button>
        </div>
      )}
    </nav>
  );
};

// Hero Section with animated text
const Hero = () => {
  const [showCore, setShowCore] = useState(false);
  const [showStrength, setShowStrength] = useState(false);
  const [showFlow, setShowFlow] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setShowCore(true), 200);
    const t2 = setTimeout(() => setShowStrength(true), 500);
    const t3 = setTimeout(() => setShowFlow(true), 800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden bg-black">
      {/* Gradient background image with 50% tint for legibility */}
      <div className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Passion Pilates Studio — Chadstone"
          className="w-full h-full object-cover object-right opacity-50"
        />
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/70"></div>
      </div>

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 lg:px-10 pt-28 pb-20" style={{fontFamily: "Georgia, 'Times New Roman', serif"}}>
        {/* Eyebrow */}
        <p className="font-sans text-[11px] tracking-[0.38em] uppercase font-semibold text-[#C97654] mb-6">
          Passion Pilates · Chadstone
        </p>

        {/* Cascading headline: Core / Strength / Flow */}
        <h1 className="font-sans font-black uppercase text-6xl md:text-8xl lg:text-9xl leading-[0.88]" style={{letterSpacing: '-0.03em'}}>
          <span className={`block text-[#C97654] ml-12 md:ml-20 transition-all duration-700 ${showCore ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>Core</span>
          <span className={`block text-[#8B2635] mt-2 ml-24 md:ml-40 transition-all duration-700 ${showStrength ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>Strength</span>
          <span className={`block text-[#9A7B6B] mt-2 ml-56 md:ml-80 transition-all duration-700 ${showFlow ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>Flow</span>
        </h1>

        {/* CTAs */}
        <div className="mt-12 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <button
            onClick={() => document.getElementById('opening-specials')?.scrollIntoView({ behavior: 'smooth' })}
            className="font-sans font-bold text-[13px] tracking-[0.25em] uppercase bg-[#8B2635] hover:bg-[#a03042] text-[#FBF6EC] px-8 py-4 rounded-sm transition-colors duration-300"
            data-testid="hero-explore-offers-btn"
          >
            Explore Offers →
          </button>
          <button
            onClick={() => document.getElementById('schedule')?.scrollIntoView({ behavior: 'smooth' })}
            className="font-sans font-bold text-[13px] tracking-[0.25em] uppercase border-2 border-[#FBF6EC]/70 hover:border-[#C97654] hover:text-[#C97654] text-[#FBF6EC] px-8 py-4 rounded-sm transition-colors duration-300"
            data-testid="hero-view-schedule-btn"
          >
            View Schedule →
          </button>

          {/* Socials */}
          <div className="flex items-center gap-2 sm:ml-3 mt-3 sm:mt-0">
            <a
              href="https://instagram.com/passionpilates.au"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-full border border-[#FBF6EC]/25 hover:border-[#C97654] hover:text-[#C97654] text-[#FBF6EC]/85 transition-colors duration-300"
              aria-label="Instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              href="https://www.facebook.com/share/1BUyxCMUip/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-full border border-[#FBF6EC]/25 hover:border-[#C97654] hover:text-[#C97654] text-[#FBF6EC]/85 transition-colors duration-300"
              aria-label="Facebook"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href="https://youtube.com/@passionpilatesau?si=XqcIu4f9MeIPYnBx"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-full border border-[#FBF6EC]/25 hover:border-[#C97654] hover:text-[#C97654] text-[#FBF6EC]/85 transition-colors duration-300"
              aria-label="YouTube"
              data-testid="hero-youtube-link"
            >
              <Youtube className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <div className="flex flex-col items-center gap-2 animate-bounce">
          <div className="w-px h-12 bg-gradient-to-b from-transparent to-[#C97654]"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-[#C97654]"></div>
        </div>
      </div>
    </section>
  );
};

// About Section
const About = () => (
  <section id="about" className="py-24 md:py-32 bg-black">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="relative group">
          <AnimatedImage 
            src={IMAGES.about} 
            alt="Passion Pilates Studio" 
            className="w-full h-[600px] object-cover transition-transform duration-700 group-hover:scale-105"
            overlayClassName="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none"
          />
        </div>
        
        <div className="space-y-8">
          <div>
            <p className="text-[#8B2635] text-sm uppercase tracking-widest mb-4 font-medium">About Us</p>
            <h2 className="font-sans font-black uppercase text-[#E8DCC4] text-4xl md:text-5xl tracking-[-0.02em] leading-[0.95] mb-6">
              Where Strength
              <span className="block mt-1">Meets Serenity</span>
            </h2>
          </div>
          
          <div className="space-y-6 text-[#E8DCC4]/80 leading-relaxed font-light">
            <p className="text-lg md:text-xl leading-relaxed font-light">
              At <span className="text-[#C97654] font-medium">Passion Pilates</span>, we are dedicated to helping individuals of <span className="text-[#C97654] font-medium">all fitness levels</span>—from beginners to advanced enthusiasts—discover their body's potential through expert Pilates instruction.
            </p>
            <p className="text-lg md:text-xl leading-relaxed font-light">
              We're committed to helping you <span className="text-[#C97654] font-medium">build core strength, improve endurance, develop agility, and strengthen your muscles for lifelong wellness.</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Classes Section
const Classes = () => {
  const classMenu = [
    { name: 'Foundation Reformer', bestFor: 'Beginners', desc: 'Focuses on learning Pilates basics like breath, core activation, posture and alignment. Slower paced with instructor guidance.', icon: Leaf, color: '#C97654' },
    { name: 'Active Reformer', bestFor: 'All levels', desc: 'A balanced full-body workout combining strength, stability and flow on the reformer.', icon: TrendingUp, color: '#B86A48' },
    { name: 'Core Sculpt', bestFor: 'All levels', desc: 'A targeted reformer class focused on deep core engagement, abdominal strength and toning. Sculpt and define your midsection through controlled, precise movements.', icon: Target, color: '#B85C38' },
    { name: 'Strength Reformer', bestFor: 'Intermediate clients', desc: 'Targets muscle building using heavier springs and slower controlled movements.', icon: Dumbbell, color: '#A5503A' },
    { name: 'Power Reformer', bestFor: 'Intermediate\u2013Advanced', desc: 'High-energy athletic style class designed to elevate heart rate and build endurance.', icon: Flame, color: '#8B2635' },
    { name: 'Flow & Stretch', bestFor: 'Recovery and flexibility', desc: 'A slower class combining mobility, deep stretching and mindful movement.', icon: Wind, color: '#9A7B6B' },
    { name: 'Mums & Bubs', bestFor: 'Postnatal Wellness', desc: 'Specialised postnatal reformer classes for new mothers and their babies. Rebuild core strength, regain endurance, and reconnect with your body in a supportive community environment. Tuesdays & Thursdays.', icon: Baby, color: '#B86A48' },
  ];

  return (
    <section id="classes" className="py-24 md:py-32 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16">
          <AnimatedImage 
            src={IMAGES.classes} 
            alt="Passion Pilates Studio" 
            className="w-full h-[500px] object-cover"
          />
        </div>

        {/* Class Menu */}
        <div className="max-w-5xl mx-auto bg-[#1e1610] rounded-2xl p-10 md:p-14">
          <div className="text-center mb-12">
            <h3 className="font-sans font-black uppercase text-[#E8DCC4] text-4xl md:text-5xl tracking-[-0.02em]">Class Menu</h3>
          </div>
          
          <div className="space-y-4">
            {classMenu.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 hover:border-[#C97654]/30 transition-colors" data-testid={`class-menu-${i}`}>
                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6 mb-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: `${item.color}20` }}>
                        <Icon className="w-5 h-5" style={{ color: item.color }} />
                      </div>
                      <h4 className="font-sans font-black uppercase tracking-[-0.02em] text-2xl md:text-3xl leading-tight" style={{ color: item.color }}>{item.name}</h4>
                    </div>
                    <span className="text-sm font-medium" style={{ color: item.color }}>Best for: {item.bestFor}</span>
                  </div>
                  <p className="text-[#E8DCC4]/70 font-light leading-relaxed ml-12 md:ml-14">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// Schedule Section
const classColors = {
  'Foundation Reformer': '#C97654',
  'Active Reformer': '#B86A48',
  'Strength Reformer': '#A5503A',
  'Power Reformer': '#8B2635',
  'Flow & Stretch Reformer': '#9A7B6B',
};

const Schedule = () => {
  const widgetContainerRef = useRef(null);
  
  const [loadWidget, setLoadWidget] = useState(false);

  // Lazy-load the MindBody widget only once the schedule section scrolls into view.
  // This avoids their tracking/analytics requests (New Relic, LaunchDarkly,
  // MindBody events, Cloudflare RUM) firing — and being flagged as errors — on
  // initial page load. The widget still loads seamlessly when the user reaches it.
  useEffect(() => {
    const el = document.getElementById('schedule');
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setLoadWidget(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: '200px' } // start loading slightly before it comes into view
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!loadWidget) return;
    const container = widgetContainerRef.current;
    if (!container) return;

    // Guard against React 18 StrictMode double-mount and HMR — only append once
    if (container.querySelector('.mindbody-widget')) return;

    const widgetDiv = document.createElement('div');
    widgetDiv.className = 'mindbody-widget';
    widgetDiv.setAttribute('data-widget-type', 'Schedules');
    widgetDiv.setAttribute('data-widget-id', '24485199377');
    container.appendChild(widgetDiv);

    // Load MindBody script if not already loaded
    if (!document.querySelector('script[src="https://brandedweb.mindbodyonline.com/embed/widget.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://brandedweb.mindbodyonline.com/embed/widget.js';
      script.async = true;
      document.body.appendChild(script);
    }

    return () => {
      // Cleanup any rendered widgets on unmount.
      if (container) {
        while (container.firstChild) container.removeChild(container.firstChild);
      }
    };
  }, [loadWidget]);

  return (
    <section id="schedule" className="py-24 md:py-32 bg-[#A0522D]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-[#FBF6EC] text-sm uppercase tracking-widest mb-4 font-medium">Class Schedule</p>
          <h2 className="font-sans font-black uppercase text-white text-4xl md:text-5xl tracking-[-0.02em] mb-6">Book Your Class</h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto font-light">
            View our live schedule and book your favorite classes. Updated in real-time.
          </p>
        </div>
        
        {/* MindBody Live Schedule Widget */}
        <div className="max-w-5xl mx-auto">
          <div 
            ref={widgetContainerRef}
            className="bg-white rounded-2xl p-4 md:p-6 shadow-2xl min-h-[500px]"
          />
        </div>
        
        <div className="text-center mt-8">
          <button 
            onClick={openMindBodyBooking}
            className="bg-[#8B2635] hover:bg-[#a03042] text-white px-8 py-3 rounded-md transition-all duration-300 font-medium"
          >
            Open Full Booking Page
          </button>
        </div>

        <p className="text-white/50 text-xs text-center mt-6 font-light max-w-3xl mx-auto">
          *Schedule updates automatically from our booking system. All classes run for 45 minutes.
        </p>
      </div>
    </section>
  );
};

// Terms and Conditions Modal
const TermsModal = ({ onClose }) => (
  <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-[#1a1a1a] border border-[#8B2635]/30 rounded-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-8 relative" onClick={e => e.stopPropagation()} data-testid="terms-modal">
      <button onClick={onClose} className="absolute top-4 right-4 text-[#E8DCC4]/60 hover:text-[#E8DCC4] transition-colors sticky" data-testid="terms-modal-close">
        <X className="w-6 h-6" />
      </button>
      <h3 className="text-2xl font-medium text-[#E8DCC4] mb-1">Membership Terms &amp; Conditions</h3>
      <p className="text-[#E8DCC4]/40 text-xs mb-1">Passion Pilates AU Pty Ltd · ABN 97 694 798 939</p>
      <p className="text-[#E8DCC4]/40 text-xs mb-1">5/49 Batesford Rd, Chadstone VIC 3148 · 0498 451 148 · passion.chadstone@gmail.com</p>
      <p className="text-[#E8DCC4]/40 text-xs mb-6">v1.2 — 18.04.26</p>
      <div className="text-[#E8DCC4]/70 text-sm space-y-6 font-light leading-relaxed">

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">1. Foundation Membership Pricing Terms</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Available to new members / memberships only.</li>
            <li>Foundation membership pricing ends whenever 100 members are secured.</li>
            <li>Campaign may change, be withdrawn or extended anytime at discretion of Passion Pilates AU management.</li>
            <li>First full monthly debit occurs during time of booking as a deposit, with progressive payments taken a month after.</li>
            <li>Membership must be activated within a month of purchase except in the case of unforeseen startup delays experienced by Passion Pilates AU. Under such circumstances, Passion will allow for all Foundation Membership activation to be extended to a month after official launch date.</li>
            <li>In cases where payments are made in advance of up to 1 month, the expiry date is based on actual activation date (i.e. client purchases a 12 month membership on monthly instalment plan on 1st January, but activates on 30th January, the final 12th month contract payment will fall on 1st of December, but membership continues until 31st December).</li>
            <li>Standard operating terms and conditions and rules and regulations apply as shown on Passion Pilates AU website or as continued below.</li>
            <li>ID Proof required for student, senior, and concession memberships.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">2. Studio Membership Terms &amp; Regulations</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Passion Pilates AU reserves the right to change conditions of Membership, hours of operation, services offered and all fees and charges, at any time, at its sole discretion and without prior notice to the Member.</li>
            <li>Any such changes will be notified to you through either a newsletter published from time to time, the website, notices in the club and/or mailing / emailing to your last known address.</li>
            <li>You agree to be bound by and to uphold our Terms &amp; Regulations.</li>
            <li>By joining any of our classes, you acknowledge that you have been provided with a copy of the current Guidelines.</li>
            <li>The Guidelines may be changed or updated by Passion Pilates AU at any time at its sole discretion and without prior notice.</li>
            <li>It is your responsibility to ensure that you correctly operate any of our studio facilities or equipment.</li>
            <li>If you are in doubt as to how to correctly operate or use any facilities or equipment, you must consult a staff member for assistance.</li>
            <li>You acknowledge that no one has made any representations (whether verbal or written) to induce you to enter this Agreement and that no verbal agreements or understandings have been made with Passion Pilates AU unless as set out in this Agreement.</li>
            <li>You must keep Passion Pilates informed of any change in your contact details, bank accounts or credit card details for payment, or any other information relevant to your Membership.</li>
            <li>You acknowledge that you have been given the option of choosing a membership based on a weekly, monthly or yearly billing agreement. These payment term options may be altered by Passion Pilates AU at any given time, normally due to payment portal system restraints. If so, all effort will be made to discuss future payment arrangements.</li>
            <li>This agreement entitles the member to the usage of equipment and resources at Passion Pilates AU in accordance with the T&amp;C's contained within.</li>
            <li>Failure to use the resources and equipment at Passion Pilates AU does not exclude the member from other obligations contained in this agreement.</li>
            <li>You acknowledge that any free/gifted membership time/training cannot be applied towards your membership minimum term.</li>
            <li>Any membership booking information / booking confirmation must be presented at reception prior to entry into a session.</li>
            <li>Refusal of providing booking information, or confirmation of booking may result in a refusal of entry.</li>
            <li>Guests of members will be charged a Casual Membership Fee and must complete the Guest Registration process on arrival at each visit.</li>
            <li>Any free entry vouchers must be presented to staff to be permitted a free session.</li>
            <li>On joining, a photograph of may be required to allow Passion Pilates AU to verify your identity each time you attend a session.</li>
            <li>Grip socks are mandatory to prevent slipping during use of our equipment and any supporting surfaces. If you do not have one, it can be purchased at reception.</li>
            <li>Unless you opt out, you agree to receive membership related updates including marketing correspondence via electronic transmission on regular basis which may include (but are not limited to) newsletters and promotional material.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">3. Cooling Off Period</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Where you have entered into an Agreement for a Minimum Membership Term, there is a seven (7) day cooling off period commencing from the join date on the agreement.</li>
            <li>You may terminate your membership on Passion Pilates AU's receipt of a CANCELLATION REQUEST FORM within the 7 day cooling off period.</li>
            <li>If a written cancellation request is not received within the seven (7) day period, the membership will continue for the agreed minimum term.</li>
            <li>You acknowledge that the following charges may apply:
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>(a) Any cost of services and/or goods provided to you during this 7 day period, and</li>
                <li>(b) If cancellation is granted, and it has been determined that the services or goods have not been used, the membership will be cancelled and a full refund may be granted.</li>
              </ul>
            </li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">4. Commitment Periods</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Your Commitment Periods include an Initial Commitment Period and may also include Ongoing Commitment Periods.</li>
            <li>You are committed to a minimum initial period of membership from the start of the first billing period following your Membership Start Date as set out in your Membership Application Form ("Commitment").</li>
            <li>On completion of your Initial Commitment Period on the end date set out in your Membership Application Form, your membership will automatically renew for further billing cycle Commitment Periods ("Ongoing Commitment Periods") unless your membership is terminated in accordance with point 3.2 or point 10.3.</li>
            <li>Each Ongoing Commitment Period will begin on the first day after your previous Ongoing Commitment Period ends.</li>
            <li>FREE/GIFTED time does not count towards the minimum membership term.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">5. Membership Rate Adjustment</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Upon the conclusion of your initial commitment period, please be aware that your membership rate may be subject to increases. The exception applies to Foundation Memberships, where your rate is locked-in for life.</li>
            <li>Any new rates will be communicated to you at least 14 days before the adjustment takes effect.</li>
            <li>This increase reflects changes in market conditions, enhancements to services, or other factors deemed necessary for maintaining quality and value.</li>
            <li>By continuing your membership beyond the initial term, you agree to the updated rate and terms.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">6. Minimum Age</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Membership is limited to persons who are at least 14 years of age.</li>
            <li>Members less than 16 years of age must be supervised by an adult when using our facilities.</li>
            <li>If you are under 16 years of age, your parent or guardian must complete your Pre-activity Readiness Questionnaire.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">7. Time Freeze</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Time Freeze can be granted for medical, pregnancy, relocation, holidays, and other reasons for a minimum of one (1) week up to a maximum of twenty-six (26) weeks.</li>
            <li>Requests for Time Freeze must be submitted in writing no less than one (1) day prior to the commencement of the Freeze.</li>
            <li>Members may not train in the studio during the period of their requested Time Freeze.</li>
            <li>An Annual Admin fee of $20 applies.</li>
            <li>Members with pre-paid memberships are required to settle Freeze Fees at the time of submitting their Request to Freeze.</li>
            <li>The Minimum Term Agreement will be extended by the period of the Time Freeze, therefore the member will be obliged to pay the minimum term agreement amount in addition to the Time Freeze period.</li>
            <li>If a freeze request has been submitted and membership fees have been paid a credit will be adjusted to the next membership fee due date.</li>
            <li>Membership dues will be adjusted (pro-rata) for members returning to the club prior to the completion of their Time Freeze.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">8. Cancellation of Membership</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>You agree that your Membership may be cancelled by Passion Pilates AU if you do not adhere to the Guidelines, or your behaviour in Passion Pilates AU is not acceptable or for any other reason that the management of Passion decides is appropriate.</li>
            <li>Passion reserves the right to refuse future Membership to you in these circumstances.</li>
            <li>Except where set out elsewhere in this Agreement, cancellation within the Minimum Membership Term is only permitted by payment of a departure fee based upon the remaining period of your Minimum Membership Term.</li>
            <li>If between $395 and $795 remains owing a total of $395 is payable.</li>
            <li>If &gt;$795 remains owing a total of 50% of remaining Membership fees is payable.</li>
            <li>The preset debit automatically continues after the end of the Minimum Membership Term, unless your membership is cancelled two (2) weeks prior to the end of your Minimum Membership Term or anytime thereafter by notifying Passion Pilates AU in writing of your intention to cancel.</li>
            <li>Membership will then be cancelled after one (1) further FULL usual payment.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">9. Transfer of Membership During Minimum Term</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>If you become unable to complete your Minimum Membership Term, you may transfer the Membership to a third party provided that:
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>an Administration Fee of $65 is paid;</li>
                <li>the transferee taking on any packages meets the package requirements otherwise current standard membership rates apply.</li>
                <li>A minimum of six (6) debits must remain and the third party must honour the remainder of the Minimum Membership Term.</li>
              </ul>
              <p className="mt-1">You must notify Passion Pilates AU in writing.</p>
            </li>
            <li>A transferred membership is non-transferable.</li>
            <li>Past members are not eligible.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">10. Downgrading of Membership During Minimum Term / Sickness or Incapacity</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>If you wish to upgrade your membership package, you may be required to complete a new membership agreement and commit to a new minimum term.</li>
            <li>Membership terms are not eligible for downgrades.</li>
            <li>If you become unable to use the club facilities by reason of permanent physical/medical incapacity, your membership can be cancelled immediately, provided that the incapacity is substantiated by a medical certificate from a qualified medical practitioner.</li>
            <li>Any cancellation will include: (a) The cost of Services already provided by the Club, and (b) An Administration Fee of $65.00.</li>
            <li>If you become unable to use the studio facilities by reason of temporary physical/medical incapacity and provide a medical certificate from a qualified medical practitioner, you are entitled to Freeze your Membership for a minimum period of one (1) week up to a maximum period of twenty-six (26) weeks.</li>
            <li>However, you may not Freeze your Membership for a longer period than set out in the medical certificate.</li>
            <li>Your Membership will automatically recommence after the date you have specified in your Request to Freeze, with no remaining Freeze facility available for any cause.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">11. Personal Training / Bookings and Appointments</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>From time to time, Passion may promote licensees or other third-party organisations who offer services such as personal training, other allied health services, nutrition consultation and/or any other special products or events.</li>
            <li>Any claim you may have as a result of an act or failure to act by such third-parties will be brought against, and will be the responsibility of that party.</li>
            <li>You hereby release, indemnify and keep indemnified Passion Pilates AU for any claim by you as a result of an act or omission by these third-party organisation(s).</li>
            <li>You acknowledge that personal trainers and therapists are available at Passion and their services shall be taken up at your sole discretion.</li>
            <li>If you are unable to attend and/or late cancel a scheduled class booking, appointment, course/workshop and do not cancel it within 12 hours of booking commencement, we reserve the right to charge a cancellation fee of $15.</li>
            <li>The fee will be added to your next scheduled membership debit.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">12. Student / Senior / Concession Memberships</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>If you are a student and acknowledge that Passion Pilates AU has provided you a preferential Student Membership Package you understand that the preferential rate will remain as long as you are a student, and undertake to provide Passion Pilates AU with a current student card every calendar year by 31st March.</li>
            <li>Passion Pilates AU will accept identification such as: University/College/Tafe/School Student ID (must be valid and display current year transport concession sticker), or a current international student visa (a photocopy of the visa contained within the passport is also acceptable).</li>
            <li>If you cannot provide a current student / seniors / concession card, your Membership fees will automatically revert to the equivalent regular membership fees at that time.</li>
            <li>If you qualify for a preferential Student / Seniors / Concession Membership Package you have a maximum of seven days from initial join date to provide a current student card, seniors card, or concession card.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">13. Pregnancy and Physical Condition</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>You acknowledge that you have to alert us to any physical conditions, including pregnancy.</li>
            <li>Each instructor will attempt to accommodate your needs and guide you into a safe routine suitable for your capacity.</li>
            <li>You must notify Passion of any change in your health and fitness so that your training regime can be appropriately modified.</li>
            <li>In the event of pregnancy, members must notify Passion by completing a Pregnancy Authorisation to Exercise form and a Pregnancy Acknowledgement form before continuing to use the facilities.</li>
            <li>You will also need to attain a letter of Medical Clearance for group exercise from your GP or OBGYN.</li>
            <li><strong>Benefits of Exercise whilst Pregnant:</strong> Regular physical activity during pregnancy may enhance psychological well-being, reduce stress, improve or maintain current fitness levels, reduce the risk of pregnancy-related complications such as gestational diabetes, back pain, oedema etc., and minimise potential perinatal complications.</li>
            <li><strong>General Guidelines:</strong> The American College of Sports Medicine (ACSM) recommends at least 150 minutes per week of physical activity, split over at least three days per week.</li>
            <li>Exercise intensity should be guided by your previous activity levels.</li>
            <li><strong>Postnatal:</strong> Once your healthcare provider has provided clearance for you to exercise again after your pregnancy, you can bring a copy of this clearance and return to Passion.</li>
            <li>Please let your Instructor know that you are postnatal and of any complications pertinent to exercise.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">14. Member's Assumption of Risk</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>You are aware that the use of the premises and its facilities and your participation in classes and programs conducted by Passion Pilates AU may involve strenuous activity and special risks.</li>
            <li>You warrant that you are physically able to participate in any classes/fitness program or use any studio facilities in which you have chosen to participate, and have not been advised otherwise by your medical practitioner.</li>
            <li>You assume the foregoing risks including risk of any negligence by Passion and its respective owners, directors, officers, employees, subcontractors or agents, and accept personal responsibility for any injury, illness, damage, loss, claim, liability or expense of any kind that you may suffer arising out of or in connection with the use of studio facilities or participation in classes/fitness programs.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">15. Corporate Memberships</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>If you are on a Corporate Membership and acknowledge that Passion Pilates AU has provided you a preferential Corporate Membership Package you understand that the preferential rate will remain as long as you are employed by a Passion Pilates AU corporate partner.</li>
            <li>Passion Pilates AU will conduct annual checks to validate your employment.</li>
            <li>In the instance that you cease to be an employee of the corporation, your rate will revert to standard membership rate.</li>
            <li>You have a maximum of seven days from initial join date to provide confirmation of your employment.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">16. Limitation of Liability</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>In consideration of the grant of Membership to you, you hereby release and forever discharge Passion Pilates AU from all actions, suits, proceedings, claims, demands, losses, damages, penalties, fines, costs and expenses howsoever arising that you may have incurred arising from or in connection with your Membership and/or use of our facilities and equipment, to the fullest extent permitted by law.</li>
            <li>Passion Pilates AU will not be liable for any loss, damage or theft of any of your property except where caused by the gross negligence of Passion Pilates AU.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">17. Payment Terms</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>A Weekly, Fortnightly, Monthly or Yearly Membership Debit may be paid by a nominated bank account or credit card.</li>
            <li>All payments charged include the following rates and fees:
              <ul className="list-none pl-0 mt-2 space-y-1 text-[#E8DCC4]/80">
                <li className="flex justify-between border-b border-[#2a2a2a] py-1"><span>Card Not Present</span><span>1.99% + $0.55</span></li>
                <li className="flex justify-between border-b border-[#2a2a2a] py-1"><span>Card Present (when available)</span><span>1.75%</span></li>
                <li className="flex justify-between border-b border-[#2a2a2a] py-1"><span>BECS (bank direct debits)</span><span>$0.88</span></li>
                <li className="flex justify-between border-b border-[#2a2a2a] py-1"><span>Tap To Pay</span><span>1.75% + $0.25</span></li>
              </ul>
            </li>
            <li>On each occasion that a scheduled payment is declined for any reason, a Payment Declined Fee of $10.00 may be charged (may change without prior notice).</li>
            <li>Should payments remain unpaid, Passion Pilates AU may suspend the Member's use of the Membership Services until all payments are paid in full.</li>
            <li>If you have opted to pre-pay your entire membership fee, any renewals thereafter must be received before the membership expires.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">18. Specific Acknowledgments</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>All members undertaking any debit membership payments must acknowledge and agree:</li>
            <li>Your nominated bank account or credit card is debited on the selected basis.</li>
            <li>The number of debits and amount is determined by your Minimum Term.</li>
            <li>Your Membership will continue after the Minimum Term, until you cancel by providing two (2) weeks written notice.</li>
            <li>Membership will then be cancelled after one (1) further full debit.</li>
            <li>PAYMENTS WILL CONTINUE TO BE DEBITED AFTER THE MINIMUM TERM UNTIL THE MEMBERSHIP IS CANCELLED BY YOU IN WRITING.</li>
            <li>Unlimited classes* is limited to one online booking per day and subject to availability to ensure all members can equally experience Passion.</li>
            <li>Should there still be available spots within 15 minutes of a class starting, you can book into any other class within the same day.</li>
          </ol>
        </div>

        <div>
          <h4 className="text-[#C97654] font-medium text-base mb-3">19. Privacy Policy</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>The Studio collects personal information for the purpose of providing the Services and managing the Participant's account. Personal information is handled in accordance with the Privacy Act 1988 (Cth) and the Studio's Privacy Policy. The Participant consents to the collection, use, and storage of their personal information for these purposes.</li>
          </ol>
        </div>

        <div className="border-t border-[#2a2a2a] pt-4 mt-4">
          <p className="text-[#E8DCC4]/40 text-xs text-center">For any further enquiries, please contact us at <a href="mailto:passion.chadstone@gmail.com" className="text-[#C97654] underline">passion.chadstone@gmail.com</a></p>
        </div>
      </div>
    </div>
  </div>
);

// Foundation Memberships
const Memberships = () => {
  const [showTerms, setShowTerms] = useState(false);
  const packages = [
    { months: '12', price: '$39', total: '$169/month or $2,028 prepaid', best: true, hasMerch: true,
      accent: '#8B2635', tint: 'rgba(139,38,53,0.12)', softText: '#F0B8C0' },
    { months: '6', price: '$45', total: '$195/month or $1,170 prepaid', hasMerch: true,
      accent: '#4A6572', tint: 'rgba(74,101,114,0.14)', softText: '#B8CCD6' },
    { months: '12', price: '$35', total: '$152/month or $1,820 prepaid', label: 'Student / Senior Foundation Membership*', hasMerch: true, best: true,
      accent: '#C9A46A', tint: 'rgba(201,164,106,0.12)', softText: '#F5DEB8' },
  ];

  const getbenefits = () => {
    return [
      'Unlimited non-transferable classes',
      'Monthly instalment option available',
      'Locked-in lifetime renewal price',
      'Custom Merch Pack included (worth $130+)',
      'Membership Freeze Flexibility*',
    ];
  };

  return (
    <section id="memberships" className="py-24 md:py-32 bg-gradient-to-b from-black via-[#0a0a0a] to-black relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#B85C38] rounded-full blur-[120px]"></div>
      </div>
      
      <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#8B2635]/20 border border-[#8B2635]/30 px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-[#8B2635]" />
            <span className="text-sm uppercase tracking-widest text-[#8B2635] font-medium">Limited Release</span>
            <Sparkles className="w-4 h-4 text-[#8B2635]" />
          </div>
          
          <h2 className="font-sans font-black uppercase text-[#E8DCC4] text-4xl md:text-6xl tracking-[-0.02em] leading-[0.95] mb-6">
            Foundation Memberships
            <span className="block mt-1 text-[#8B2635]">Now on Sale</span>
          </h2>
          <p className="text-lg md:text-xl text-[#E8DCC4]/80 max-w-3xl mx-auto leading-relaxed font-light">
            Become an exclusive Founding Member and enjoy lifetime benefits including locked-in prices and 10% off merchandise. Limited to the first 100 members.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 mb-12 max-w-5xl mx-auto">
          {packages.map((pkg, i) => (
            <div
              key={pkg.label || `${pkg.months}-${pkg.price}`}
              onClick={openMindBodyMembership}
              data-testid={`membership-card-${i}`}
              className="rounded-xl p-8 transition-all duration-500 relative overflow-hidden group cursor-pointer hover:scale-[1.02] hover:-translate-y-1"
              style={{
                backgroundColor: '#1a1a1a',
                backgroundImage: `linear-gradient(155deg, ${pkg.tint} 0%, rgba(26,26,26,0) 65%)`,
                border: `1px solid ${pkg.accent}`,
                boxShadow: `0 20px 60px -30px ${pkg.accent}`,
              }}
            >
              {pkg.best && (
                <div
                  className="absolute -top-px left-1/2 -translate-x-1/2 text-white text-xs px-4 py-1 rounded-b-md font-bold uppercase tracking-widest"
                  style={{ backgroundColor: pkg.accent }}
                >
                  Best Value
                </div>
              )}

              <div
                className="absolute top-0 right-0 w-32 h-32 rounded-full blur-2xl transition-all duration-500 group-hover:opacity-60 opacity-30"
                style={{ backgroundColor: pkg.accent }}
              ></div>

              <div className="relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3
                      className="font-sans font-black uppercase tracking-[-0.02em] text-2xl md:text-3xl mb-2"
                      style={{ color: pkg.accent }}
                    >
                      {pkg.months} Months
                    </h3>
                    <p className="text-sm text-[#E8DCC4]/60 uppercase tracking-wider font-bold">{pkg.label || 'Foundation Membership'}</p>
                  </div>
                  <Calendar className="w-8 h-8" style={{ color: pkg.accent }} />
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-5xl font-bold" style={{ color: pkg.accent }}>{pkg.price}</span>
                    <span className="text-lg text-[#E8DCC4]/60 font-light">per week</span>
                  </div>
                  <p className="text-sm text-[#E8DCC4]/60 font-light">{pkg.total}</p>
                </div>

                <ul className="space-y-3 mb-6">
                  {getbenefits().map((benefit, j) => (
                    <li key={benefit} className="flex items-start gap-3 text-sm text-[#E8DCC4]/85 font-light">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: pkg.accent }} />
                      <span className={j === 0 || j === 2 || j === 3 ? 'font-bold' : ''}>{benefit}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={(e) => { e.stopPropagation(); openMindBodyMembership(); }}
                  className="w-full text-white py-3 rounded-md transition-all duration-300 font-sans font-bold tracking-[0.15em] uppercase text-[12px] hover:brightness-110"
                  style={{ backgroundColor: pkg.accent }}
                >
                  Claim {pkg.months} Month Membership
                </button>
              </div>
            </div>
          ))}
        </div>
        
        <p className="text-[#E8DCC4]/50 text-xs text-center max-w-3xl mx-auto">
          Foundation Memberships are limited release. Pay upfront or choose monthly instalments. Memberships are non-transferable and cannot be cancelled until the end of the commitment period. *Membership Freeze Flexibility is subject to{' '}
          <button onClick={() => setShowTerms(true)} className="underline hover:text-[#C97654] transition-colors" data-testid="terms-link">Terms and Conditions</button>. 
          *Student / Senior Foundation Membership requires valid student ID or proof of senior status to be presented. Subject to studio{' '}
          <button onClick={() => setShowTerms(true)} className="underline hover:text-[#C97654] transition-colors" data-testid="terms-link-general">Terms and Conditions</button>. Limited availability.
        </p>
      </div>

      {/* Terms and Conditions Modal */}
      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
    </section>
  );
};

// Trial Offer Section — highlighted single-focus promo
const OpeningSpecials = () => {
  const handleTrialClick = () => {
    window.open('https://clients.mindbodyonline.com/classic/ws?studioid=5751621&stype=41&sTG=23&prodId=100052', '_blank');
  };

  return (
    <section id="opening-specials" className="py-20 md:py-28 bg-gradient-to-b from-[#8B2635]/30 to-[#0a0a0a]">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#8B2635]/40 border border-[#8B2635] px-6 py-2 rounded-full mb-6">
            <Sparkles className="w-5 h-5 text-[#C97654]" />
            <span className="text-sm uppercase tracking-widest text-[#C97654] font-bold">Limited Time Only</span>
            <Sparkles className="w-5 h-5 text-[#C97654]" />
          </div>
          <h2 className="font-sans font-black uppercase text-[#E8DCC4] text-4xl md:text-6xl tracking-[-0.02em] mb-4">Trial Offer</h2>
          <p className="text-xl md:text-2xl text-[#C97654] font-medium mb-2">Try Passion Pilates for 2 Weeks</p>
          <p className="text-[#E8DCC4]/70 font-light">Brand new to the studio? This is the perfect way to discover us.</p>
        </div>

        <button
          onClick={handleTrialClick}
          data-testid="trial-offer-cta"
          className="block w-full bg-[#1a1a1a] border-2 border-[#C97654] rounded-2xl p-8 md:p-10 text-center hover:scale-[1.02] hover:bg-[#2a2a2a] transition-all duration-300 cursor-pointer shadow-2xl"
        >
          {/* Crossed-out vs new class count */}
          <div className="flex items-baseline justify-center gap-3 mb-3">
            <span className="text-[#E8DCC4]/40 text-3xl md:text-4xl font-light line-through decoration-2">5</span>
            <span className="text-[#C97654] text-5xl md:text-6xl font-bold">6</span>
            <span className="text-[#E8DCC4]/80 text-2xl md:text-3xl font-light">classes</span>
          </div>
          <p className="text-[#E8DCC4]/70 text-base md:text-lg font-light mb-6">over 2 weeks</p>

          <div className="border-t border-[#2a2a2a] pt-6">
            <p className="text-[#E8DCC4]/60 text-xs uppercase tracking-[0.25em] mb-2">just</p>
            <p className="text-6xl md:text-7xl font-bold text-[#C97654] mb-2">$55</p>
            <div className="inline-flex items-center gap-2 bg-[#8B2635]/30 border border-[#8B2635]/60 px-4 py-1.5 rounded-full mt-3">
              <span className="text-[#FFB89A] text-xs font-bold uppercase tracking-widest">+1 bonus class</span>
            </div>
          </div>

          <p className="text-white bg-[#B85C38] hover:bg-[#C97654] inline-block mt-8 px-8 py-3 rounded-md font-medium transition-all duration-300">
            Start Your Trial →
          </p>
        </button>

        <p className="text-[#E8DCC4]/50 text-sm text-center mt-8 font-light">
          *Limited-time bonus: receive 6 classes instead of 5 when you purchase your trial. New members only.
        </p>
      </div>
    </section>
  );
};

// Pricing Section
const Pricing = () => {
  const [emblaApi, setEmblaApi] = useState(null);

  // 3D deck-of-cards effect — hook into embla's scroll to transform each slide.
  useEffect(() => {
    if (!emblaApi) return;
    const applyTransforms = () => {
      const engine = emblaApi.internalEngine();
      const scrollProgress = emblaApi.scrollProgress();
      const snapList = emblaApi.scrollSnapList();
      const nodes = emblaApi.slideNodes();
      const slideCount = nodes.length;
      // Map current scrollProgress (0..1) to a floating "slide index" position
      // — this gives a continuous distance from each slide.
      const maxSnap = snapList.length - 1 || 1;
      const currentFloat = scrollProgress * maxSnap;

      nodes.forEach((node, i) => {
        const inner = node.querySelector('[data-deck-card]');
        if (!inner) return;
        const diff = i - currentFloat;
        const abs = Math.min(Math.abs(diff), 3); // clamp so far cards don't over-rotate
        const rotateY = diff * -14; // tilt away from centre
        const scale = 1 - abs * 0.06;
        const translateZ = -abs * 90;
        const translateX = diff * 6; // subtle stagger
        const opacity = Math.max(0.35, 1 - abs * 0.22);
        inner.style.transform = `translate3d(${translateX}%, 0, ${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`;
        inner.style.opacity = String(opacity);
        inner.style.zIndex = String(100 - Math.round(abs * 10));
      });
      // Prevent engine cleanup from stripping our transforms
      engine.translate.toggleActive(true);
    };
    applyTransforms();
    emblaApi.on('scroll', applyTransforms);
    emblaApi.on('select', applyTransforms);
    emblaApi.on('reInit', applyTransforms);
    return () => {
      emblaApi.off('scroll', applyTransforms);
      emblaApi.off('select', applyTransforms);
      emblaApi.off('reInit', applyTransforms);
    };
  }, [emblaApi]);

  const prices = [
    { name: 'Casual Class', subtitle: 'Single class pass', price: '$35',
      features: ['Valid for any group class', 'No expiry date', 'Book online anytime'],
      accent: '#E8804C', tint: 'rgba(232,128,76,0.10)', border: 'rgba(232,128,76,0.45)' },
    { name: '2x Classes Weekly', subtitle: 'Consistent fitness routine', price: '$49/wk',
      features: ['Attend up to 2 classes per week', 'Feel stronger after each workout', 'No contracts or commitments', 'Flexible - just results!'],
      accent: '#B85C38', tint: 'rgba(184,92,56,0.12)', border: 'rgba(184,92,56,0.5)' },
    { name: '3x Classes Weekly', subtitle: 'Recommended for best results', price: '$59/wk',
      features: ['Attend up to 3 classes per week', 'Optimal for transformation', 'No contracts or commitments', 'Real results guaranteed!'],
      best: true, accent: '#8B2635', tint: 'rgba(139,38,53,0.15)', border: 'rgba(139,38,53,0.6)' },
    { name: '10 Class Pack', subtitle: 'Best value for regular practice', price: '$300',
      features: ['Save $50 on regular pricing', '6 month expiry', 'Shareable with family'],
      accent: '#C9A46A', tint: 'rgba(201,164,106,0.10)', border: 'rgba(201,164,106,0.45)' },
    { name: '20 Class Pack', subtitle: 'Maximum savings · stack & share', price: '$550',
      features: ['Best value per class', '12 month expiry', 'Shareable with family'],
      accent: '#7C8B5C', tint: 'rgba(124,139,92,0.12)', border: 'rgba(124,139,92,0.5)' },
    { name: 'Private Sessions', subtitle: 'One-on-one · 60 minutes', price: '$100',
      features: ['1-on-1 personalised coaching', 'Ideal for injuries or specific goals', 'Book any available time slot', 'Expert instructor guidance'],
      accent: '#4A6572', tint: 'rgba(74,101,114,0.14)', border: 'rgba(74,101,114,0.55)' },
    { name: 'Private Package', subtitle: '5 private personalised sessions', price: 'From $450',
      features: ['Personalised programming', 'Flexible scheduling', '3 month expiry', 'Progress tracking'],
      accent: '#9A5B70', tint: 'rgba(154,91,112,0.12)', border: 'rgba(154,91,112,0.5)' },
  ];

  return (
    <section id="pricing" className="py-24 md:py-32 bg-[#0a0a0a]" style={{fontFamily: "Georgia, 'Times New Roman', serif"}}>
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        {/* Editorial header */}
        <div className="mb-16 max-w-3xl">
          <p className="font-sans text-[11px] tracking-[0.38em] uppercase font-semibold text-[#C97654] mb-4">
            Pricing
          </p>
          <h2 className="font-sans font-black uppercase text-[#FBF6EC] text-5xl md:text-7xl leading-[0.9] tracking-[-0.03em]">
            Other<br/>
            <span className="text-[#C97654]">Packages.</span>
          </h2>
          <div className="h-px bg-[#FBF6EC]/20 my-8"></div>
          <p className="font-sans text-[15px] md:text-[16px] leading-[1.65] text-[#E8DCC4]/75">
            Flexible options designed to support your wellness journey — no contracts unless you choose one.
          </p>
        </div>

        {/* Cards carousel — swipeable / arrow-navigable with 3D deck-shuffle */}
        <Carousel
          opts={{ align: "start", loop: false }}
          setApi={setEmblaApi}
          className="w-full"
          style={{ perspective: "1800px" }}
          data-testid="other-packages-carousel"
        >
          <CarouselContent className="-ml-4" style={{ transformStyle: "preserve-3d" }}>
            {prices.map((item, i) => (
              <CarouselItem
                key={item.name}
                className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3"
                data-testid={`other-packages-slide-${i}`}
                style={{ transformStyle: "preserve-3d" }}
              >
                <div
                  data-deck-card
                  className="relative p-8 flex flex-col h-full will-change-transform overflow-hidden"
                  style={{
                    transformStyle: "preserve-3d",
                    transformOrigin: "center center",
                    backgroundColor: `#0a0a0a`,
                    backgroundImage: `linear-gradient(160deg, ${item.tint} 0%, rgba(10,10,10,0) 60%)`,
                    borderTop: `3px solid ${item.accent}`,
                    border: `1px solid ${item.border}`,
                    borderTopWidth: '3px',
                    boxShadow: `0 20px 60px -30px ${item.accent}80`,
                  }}
                >
                  {item.best ? (
                    <p
                      className="font-sans text-[10px] tracking-[0.32em] uppercase font-bold mb-4 inline-flex items-center gap-2"
                      style={{ color: item.accent }}
                    >
                      <span
                        className="inline-block w-6 h-[2px]"
                        style={{ backgroundColor: item.accent }}
                      ></span>
                      Best Value
                    </p>
                  ) : (
                    <p
                      className="font-sans text-[10px] tracking-[0.32em] uppercase font-bold mb-4"
                      style={{ color: item.accent, opacity: 0.85 }}
                    >
                      Package
                    </p>
                  )}

                  <h3
                    className="text-[32px] md:text-[36px] leading-[1.02] font-normal tracking-[-0.7px] mb-2"
                    style={{ color: item.accent }}
                  >
                    {item.name}
                  </h3>
                  <p className="font-sans text-[13px] text-[#E8DCC4]/60 mt-1 mb-5 italic">{item.subtitle}</p>

                  {/* Price */}
                  <p
                    className="font-bold text-[48px] leading-none tracking-[-1.2px]"
                    style={{ color: item.accent }}
                  >
                    {item.price}
                  </p>

                  {/* Hairline */}
                  <div
                    className="h-px my-6"
                    style={{ backgroundColor: `${item.accent}55` }}
                  ></div>

                  {/* Features list — editorial bullets */}
                  <ul className="space-y-2.5 mb-8 font-sans">
                    {item.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-[14px] leading-[1.55] text-[#E8DCC4]/85">
                        <span className="font-bold mt-[1px]" style={{ color: item.accent }}>·</span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Button — theme-coloured for best; ghost with accent hover otherwise */}
                  <button
                    onClick={() => {
                      window.open('https://clients.mindbodyonline.com/classic/ws?studioid=5751621&stype=41&sTG=23', '_blank');
                    }}
                    data-testid={`other-packages-cta-${i}`}
                    className="mt-auto font-sans font-bold text-[12px] tracking-[0.25em] uppercase px-6 py-3.5 rounded-sm transition-all duration-300 hover:brightness-110"
                    style={{
                      backgroundColor: item.accent,
                      color: '#FBF6EC',
                    }}
                  >
                    Select Package →
                  </button>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>

          {/* Arrows — editorial styling */}
          <CarouselPrevious
            data-testid="other-packages-prev"
            className="hidden md:inline-flex -left-4 lg:-left-6 bg-[#0a0a0a] border-[#FBF6EC]/30 text-[#FBF6EC] hover:bg-[#C97654] hover:border-[#C97654] hover:text-[#0a0a0a]"
          />
          <CarouselNext
            data-testid="other-packages-next"
            className="hidden md:inline-flex -right-4 lg:-right-6 bg-[#0a0a0a] border-[#FBF6EC]/30 text-[#FBF6EC] hover:bg-[#C97654] hover:border-[#C97654] hover:text-[#0a0a0a]"
          />
        </Carousel>

        {/* Mobile swipe hint */}
        <p
          data-testid="other-packages-swipe-hint"
          className="md:hidden mt-8 font-sans text-[14px] tracking-[0.28em] uppercase font-bold text-center flex items-center justify-center gap-2"
          style={{ color: '#C1272D' }}
        >
          <span className="animate-pulse">←</span> swipe to see more <span className="animate-pulse">→</span>
        </p>

        {/* Corporate / Private Hire — editorial banner */}
        <div className="mt-16 relative bg-[#0a0a0a] border border-[#FBF6EC]/15 p-8 md:p-12">
          <p className="font-sans font-black uppercase tracking-[0.15em] text-[#C97654] text-lg md:text-xl mb-4">
            Corporate &amp; Private Hire
          </p>

          <div className="grid md:grid-cols-[1.4fr_1fr] gap-10 items-start">
            <div>
              <h3 className="text-[#FBF6EC] text-[32px] md:text-[44px] leading-[1.05] font-normal tracking-[-1px]">
                Hens, birthdays,<br/>
                team building &amp; <em className="italic text-[#C97654]">more.</em>
              </h3>

              {/* Editorial icon row */}
              <div className="flex items-center gap-5 mt-6 mb-6 text-[#C97654]/85">
                <PartyPopper className="w-5 h-5" />
                <Cake className="w-5 h-5" />
                <Gift className="w-5 h-5" />
                <Crown className="w-5 h-5" />
                <Trophy className="w-5 h-5" />
                <Martini className="w-5 h-5" />
              </div>

              <p className="font-sans text-[15px] md:text-[16px] leading-[1.65] text-[#E8DCC4]/80 max-w-xl">
                Hire our boutique studio for <em className="italic text-[#C97654] not-italic font-semibold">Corporate sessions</em>, <em className="italic text-[#C97654] not-italic font-semibold">Private Venues</em> and <em className="italic text-[#C97654] not-italic font-semibold">Function Hire</em> — perfect for hens celebrations, birthdays, team building and special events.
              </p>
            </div>

            <div className="md:border-l md:border-[#FBF6EC]/15 md:pl-10">
              <p className="font-sans text-[10px] tracking-[0.3em] uppercase font-semibold text-[#FBF6EC]/55 mb-2">From</p>
              <p className="font-bold text-[56px] leading-none tracking-[-2px] text-[#C97654] mb-6">$315</p>
              <a
                href="mailto:passion.chadstone@gmail.com?subject=Corporate%20%2F%20Private%20Venue%20Enquiry"
                data-testid="corporate-hire-cta"
                className="inline-block font-sans font-bold text-[12px] tracking-[0.25em] uppercase bg-[#8B2635] hover:bg-[#a03042] text-[#FBF6EC] px-6 py-3.5 rounded-sm transition-colors duration-300"
              >
                Enquire by Email →
              </a>
              <p className="font-sans text-[12px] text-[#E8DCC4]/55 mt-3 italic">
                passion.chadstone@gmail.com
              </p>
            </div>
          </div>
        </div>

        <p className="font-sans text-[12px] tracking-[0.15em] uppercase text-[#E8DCC4]/45 text-center mt-10">
          All packages include complimentary studio amenities &amp; equipment
        </p>
      </div>
    </section>
  );
};

// FAQ Section
const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    { q: 'What should I wear to class?', a: <span>Wear comfortable, form-fitting activewear that allows you to move freely. <strong>Grip socks are required for reformer classes and available for purchase at the studio.</strong> Bring along a sweat towel and water bottle. Water refilling station means you'll always stay cool and hydrated.</span> },
    { q: 'Do I need prior Pilates experience?', a: 'No prior experience is necessary! We welcome all levels from complete beginners to advanced practitioners. Our instructors will guide you through every movement.' },
    { q: 'What is the cancellation policy?', a: <span>Classes can be cancelled up to 12 hours before the scheduled start time. <span className="text-[#8B2635] font-medium">Late cancellations and no-shows will result in an extra cancellation fee of $15 (even if package is unlimited or credits are deducted) to ensure fairness</span> and that no one misses out on a limited class slot.</span> },
    { q: 'What are the studio guidelines?', a: (
      <div className="space-y-4 text-[#E8DCC4]/80 leading-relaxed">
        <p className="italic text-[#C97654]">Movement with Purpose — At Passion Pilates, we are committed to creating a refined, welcoming, and high-performance environment for every client. To ensure the best experience for all, we ask that you respect the following studio guidelines:</p>

        <div>
          <p className="text-[#C97654] font-medium mb-1">1. Bookings &amp; Attendance</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>All classes must be booked in advance via our website or booking platform.</li>
            <li>Please arrive at least 5 minutes early. Late entry may not be permitted once class has commenced.</li>
            <li>If you are new, we recommend arriving 10 minutes early for a brief introduction.</li>
          </ul>
        </div>

        <div>
          <p className="text-[#C97654] font-medium mb-1">2. Cancellation &amp; No-Show Policy</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>We operate a 12-hour cancellation policy.</li>
            <li>Cancellations made within 12 hours of class will incur a $15 late cancellation fee.</li>
            <li>Failure to attend a booked class will result in a $15 no-show fee.</li>
            <li>Unlimited members may also have their booking privileges temporarily restricted for repeated late cancellations or no-shows.</li>
            <li>Early cancellations (outside the 12-hour window) can be made free of charge via your account.</li>
          </ul>
        </div>

        <div>
          <p className="text-[#C97654] font-medium mb-1">3. Waitlist Policy</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>If a class is full, you may join the waitlist.</li>
            <li>You will be automatically added to the class if a spot becomes available.</li>
            <li>If you are added within the 12-hour window, and do not cancel within 3 hours of the time of notification, the cancellation policy still applies.</li>
          </ul>
        </div>

        <div>
          <p className="text-[#C97654] font-medium mb-1">4. Respect for Staff &amp; Community</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>We pride ourselves on a supportive and respectful environment.</li>
            <li>Clients must treat instructors and fellow members with courtesy and professionalism at all times.</li>
            <li>Aggressive, disruptive, or disrespectful behaviour will not be tolerated and may result in suspension or termination of membership.</li>
          </ul>
        </div>

        <div>
          <p className="text-[#C97654] font-medium mb-1">5. Studio Etiquette</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Grip socks are required for hygiene and safety.</li>
            <li>Please wipe down equipment after use.</li>
            <li>Phones should be kept on silent and used only when necessary.</li>
            <li>No filming or photography without prior approval.</li>
          </ul>
        </div>

        <div>
          <p className="text-[#C97654] font-medium mb-1">6. Health &amp; Safety</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Please inform your instructor of any injuries, medical conditions, or pregnancy prior to class.</li>
            <li>Follow all instructor cues carefully — exercises may be modified for your safety.</li>
            <li>Passion Pilates reserves the right to refuse participation or entry if it is deemed unsafe.</li>
            <li>Entry may also be refused if a valid liability waiver has not been completed and signed prior to attending class.</li>
          </ul>
        </div>

        <div>
          <p className="text-[#C97654] font-medium mb-1">7. Late Entry Policy</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>For safety and class flow, entry may be refused if you arrive more than 5 minutes late.</li>
          </ul>
        </div>

        <div>
          <p className="text-[#C97654] font-medium mb-1">8. Personal Belongings</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Storage areas are provided for convenience; however, all items are left at your own risk.</li>
            <li>Please avoid bringing valuables into the studio.</li>
          </ul>
        </div>

        <div>
          <p className="text-[#C97654] font-medium mb-1">9. Membership &amp; Usage Conduct</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Memberships and class packs (except 10-class packs) are non-transferable unless otherwise approved.</li>
          </ul>
        </div>

        <div>
          <p className="text-[#C97654] font-medium mb-1">10. Creating the Passion Pilates Experience</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>We are more than a workout — we are a space for elevated movement, focus, and transformation.</li>
            <li>We ask every client to contribute to the calm, luxe and community atmosphere that defines Passion Pilates.</li>
          </ul>
        </div>

        <p className="italic text-[#C97654] pt-2">Thank you — we appreciate your cooperation in helping us maintain a premium studio experience for all.</p>
      </div>
    ) },
    { q: 'Can I share my class pack with family or friends?', a: 'The 10 Class Pack is shareable with immediate family members. Foundation Memberships and any Unlimited Membership packages are non-transferable and for individual use only.' },
    { q: 'Is Mums & Bubs suitable for all ages?', a: 'Mums & Bubs classes are designed for mothers with babies from 6 weeks to crawling age. Please consult your healthcare provider before starting any postnatal exercise.' },
    { q: "What's included in the Foundation Membership merch pack?", a: 'Each 6 and 12 month Foundation Membership includes a Custom Merch Pack worth over $130, featuring: Fressko branded keep cup, Passion Grip Socks, Passion Pilates T-Shirt, Organic Somage tea, and a Custom tote-bag.' },
    { q: 'Can I freeze my membership?', a: 'Foundation Memberships include Membership Freeze Flexibility. It is granted for medical, pregnancy and business relocation reasons for a minimum of one (1) week up to a maximum of twenty-six (26) weeks. For holidays/other reasons, it is for a minimum of one (1) week up to a maximum of twelve (12) weeks. The Freeze request must be accompanied by a doctor\'s certificate (medical/pregnancy) or an employer\'s letter (business relocation) at the time of the Freeze request.' },
    { q: 'Do you offer private sessions for injuries or special requirements?', a: 'Yes! Our private sessions are perfect for those with injuries, special requirements, or specific goals. Our instructors will create a personalized program for you.' },
  ];

  return (
    <section id="faq" className="py-24 md:py-32 bg-black">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[#8B2635] text-sm uppercase tracking-widest mb-4 font-medium">FAQ</p>
          <h2 className="font-sans font-black uppercase text-[#E8DCC4] text-4xl md:text-5xl tracking-[-0.02em] leading-[0.95] mb-6">Frequently Asked Questions</h2>
          <p className="text-lg text-[#E8DCC4]/70 max-w-2xl mx-auto font-light">
            Everything you need to know about Passion Pilates
          </p>
        </div>
        
        <div className="space-y-4">
          {faqs.map((faq, i) => {
            const palette = [
              { accent: '#E8804C', text: '#FFD9C0' }, // terracotta
              { accent: '#B85C38', text: '#F5C4A8' }, // rust
              { accent: '#C97654', text: '#FFDCC7' }, // warm coral
              { accent: '#C9A46A', text: '#F5DEB8' }, // warm gold
              { accent: '#7C8B5C', text: '#D8DFC4' }, // sage
              { accent: '#4A6572', text: '#B8CCD6' }, // slate blue
              { accent: '#9A5B70', text: '#E8C5D0' }, // plum
              { accent: '#8B2635', text: '#F0B8C0' }, // burgundy
            ];
            const c = palette[i % palette.length];
            const isOpen = openIndex === i;
            return (
              <div
                key={faq.q}
                data-testid={`faq-${i}`}
                className="px-6 transition-colors duration-300"
                style={{
                  backgroundColor: '#1a1a1a',
                  border: `1px solid ${isOpen ? c.accent : '#2a2a2a'}`,
                  boxShadow: isOpen ? `0 12px 40px -20px ${c.accent}` : 'none',
                }}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className="w-full flex items-center justify-between py-6 text-left"
                >
                  <span
                    className="text-lg font-semibold transition-colors duration-300"
                    style={{ color: c.accent }}
                  >
                    {faq.q}
                  </span>
                  <ChevronDown
                    className={`w-6 h-6 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                    style={{ color: c.accent, opacity: isOpen ? 1 : 0.7 }}
                  />
                </button>
                {isOpen && (
                  <div
                    className="pb-6 leading-relaxed font-light"
                    style={{ color: c.text }}
                  >
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        <div className="text-center mt-12">
          <p className="text-[#E8DCC4]/60 mb-4">Still have questions?</p>
          <button 
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="text-[#8B2635] hover:text-[#a03042] transition-colors duration-300 font-medium"
          >
            Get in touch with us →
          </button>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', classType: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', classType: '', message: '' });
      }
    } catch (err) {
      console.error('Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Studio Photo */}
        <div className="mb-16 rounded-xl">
          <AnimatedImage 
            src="https://customer-assets.emergentagent.com/job_passion-preview/artifacts/jwtn3817_BA30A84E-D3EE-4950-B4D3-9549162B25D9.png" 
            alt="Passion Pilates Studio Storefront" 
            className="w-full h-[400px] object-cover rounded-xl"
            overlayClassName="absolute inset-0 bg-[#C97654]/40 mix-blend-multiply rounded-xl"
          />
        </div>
        
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-[#C97654] text-sm uppercase tracking-widest mb-4 font-medium">Get In Touch</p>
            <h2 className="font-sans font-black uppercase text-[#E8DCC4] text-4xl md:text-5xl tracking-[-0.02em] mb-8">Join Our Community</h2>
            
            <div className="space-y-8 mb-12">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#1a1a1a] border border-[#2a2a2a]">
                  <MapPin className="w-5 h-5 text-[#C97654]" />
                </div>
                <div>
                  <p className="text-sm text-[#E8DCC4]/60 uppercase tracking-wider mb-1 font-medium">Address</p>
                  <p className="text-[#E8DCC4] text-lg font-light">5/49 Batesford Rd, Chadstone, VIC 3148</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#1a1a1a] border border-[#2a2a2a]">
                  <Mail className="w-5 h-5 text-[#C97654]" />
                </div>
                <div>
                  <p className="text-sm text-[#E8DCC4]/60 uppercase tracking-wider mb-1 font-medium">Email</p>
                  <a href="mailto:passion.chadstone@gmail.com" className="text-[#E8DCC4] text-lg hover:text-[#C97654] transition-colors duration-300 font-light">
                    passion.chadstone@gmail.com
                  </a>
                </div>
              </div>

              <a href="tel:+61498451148" className="flex items-start gap-4 group">
                <div className="p-3 bg-[#1a1a1a] border border-[#2a2a2a] group-hover:border-[#C97654] transition-colors duration-300">
                  <svg className="w-5 h-5 text-[#C97654]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-[#E8DCC4]/60 uppercase tracking-wider mb-1 font-medium">Phone</p>
                  <span className="text-[#E8DCC4] text-lg group-hover:text-[#C97654] transition-colors duration-300 font-light">
                    0498 451 148
                  </span>
                </div>
              </a>
              
              <div>
                <p className="text-sm text-[#E8DCC4]/60 uppercase tracking-wider mb-4">Follow Us</p>
                <div className="flex gap-4 flex-wrap">
                  <a
                    href="https://instagram.com/passionpilates.au"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    data-testid="contact-instagram-link"
                    className="group relative w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 hover:-translate-y-1"
                    style={{ background: 'linear-gradient(135deg, #F58529 0%, #DD2A7B 45%, #8134AF 80%, #515BD4 100%)' }}
                  >
                    <Instagram className="w-6 h-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)] transition-transform duration-300 group-hover:rotate-[8deg]" />
                    <span className="absolute -bottom-6 text-[10px] tracking-[0.2em] uppercase text-[#E8DCC4]/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">Instagram</span>
                  </a>
                  <a
                    href="https://www.facebook.com/share/1BUyxCMUip/?mibextid=wwXIfr"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    data-testid="contact-facebook-link"
                    className="group relative w-14 h-14 rounded-full flex items-center justify-center bg-[#1877F2] transition-all duration-300 hover:scale-110 hover:-translate-y-1 hover:shadow-[0_0_24px_rgba(24,119,242,0.7)]"
                  >
                    <Facebook className="w-6 h-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)] transition-transform duration-300 group-hover:rotate-[-8deg]" />
                    <span className="absolute -bottom-6 text-[10px] tracking-[0.2em] uppercase text-[#E8DCC4]/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">Facebook</span>
                  </a>
                  <a
                    href="https://youtube.com/@passionpilatesau?si=XqcIu4f9MeIPYnBx"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="YouTube"
                    data-testid="contact-youtube-link"
                    className="group relative w-14 h-14 rounded-full flex items-center justify-center bg-[#FF0000] transition-all duration-300 hover:scale-110 hover:-translate-y-1 hover:shadow-[0_0_24px_rgba(255,0,0,0.7)]"
                  >
                    <Youtube className="w-6 h-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)] transition-transform duration-300 group-hover:rotate-[8deg]" />
                    <span className="absolute -bottom-6 text-[10px] tracking-[0.2em] uppercase text-[#E8DCC4]/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">YouTube</span>
                  </a>
                </div>
                <p className="text-sm text-[#E8DCC4]/70 mt-6">@passionpilates.au</p>
              </div>
            </div>
            
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] p-8">
              <h3 className="font-sans font-black uppercase text-[#E8DCC4] text-2xl md:text-3xl tracking-[-0.02em] mb-6">Studio Hours</h3>
              <div className="flex justify-between items-start text-[#E8DCC4]/70 gap-4">
                <div>
                  <p>Monday - Sunday</p>
                  <p className="text-[#E8DCC4]/50 text-xs mt-1 font-light italic">Open when classes are scheduled</p>
                </div>
                <span className="text-[#C97654] whitespace-nowrap">6:00 AM - 8:30 AM</span>
              </div>
            </div>
          </div>
          
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] p-8 lg:p-12">
            <h3 className="font-sans font-black uppercase text-[#C97654] text-3xl md:text-4xl tracking-[-0.02em] mb-6">Enquire Now</h3>
            <p className="text-[#E8DCC4]/70 mb-8">
              New to Passion Pilates? Fill out the form below and we'll help you get started on your wellness journey.
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm text-[#E8DCC4]/60 uppercase tracking-wider mb-2">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter your name"
                  className="w-full bg-black border border-[#2a2a2a] px-4 py-3 text-[#E8DCC4] focus:outline-none focus:border-[#C97654] transition-colors duration-300 placeholder-[#E8DCC4]/30"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm text-[#E8DCC4]/60 uppercase tracking-wider mb-2">Email Address</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="Enter your email"
                  className="w-full bg-black border border-[#2a2a2a] px-4 py-3 text-[#E8DCC4] focus:outline-none focus:border-[#C97654] transition-colors duration-300 placeholder-[#E8DCC4]/30"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm text-[#E8DCC4]/60 uppercase tracking-wider mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="Enter your phone"
                  className="w-full bg-black border border-[#2a2a2a] px-4 py-3 text-[#E8DCC4] focus:outline-none focus:border-[#C97654] transition-colors duration-300 placeholder-[#E8DCC4]/30"
                />
              </div>
              
              <div>
                <label className="block text-sm text-[#E8DCC4]/60 uppercase tracking-wider mb-2">Preferred Class</label>
                <select
                  value={formData.classType}
                  onChange={(e) => setFormData({...formData, classType: e.target.value})}
                  data-testid="enquire-class-select"
                  className="w-full bg-black border border-[#2a2a2a] px-4 py-3 text-[#E8DCC4] focus:outline-none focus:border-[#C97654] transition-colors duration-300 font-semibold"
                >
                  <option value="" style={{ backgroundColor: '#0a0a0a', color: '#E8DCC4', fontWeight: 400 }}>Select a class</option>
                  <option value="reformer" style={{ backgroundColor: '#0a0a0a', color: '#E8804C', fontWeight: 700 }}>Reformer Pilates</option>
                  <option value="corporate" style={{ backgroundColor: '#0a0a0a', color: '#8B2635', fontWeight: 700 }}>Corporate / Private Hire</option>
                  <option value="private" style={{ backgroundColor: '#0a0a0a', color: '#4A6572', fontWeight: 700 }}>Private Sessions</option>
                  <option value="mumsbubs" style={{ backgroundColor: '#0a0a0a', color: '#C9A46A', fontWeight: 700 }}>Mums &amp; Bubs</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-[#E8DCC4]/60 uppercase tracking-wider mb-2">Message (Optional)</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Tell us about your goals and experience"
                  rows={4}
                  className="w-full bg-black border border-[#2a2a2a] px-4 py-3 text-[#E8DCC4] focus:outline-none focus:border-[#C97654] transition-colors duration-300 resize-none placeholder-[#E8DCC4]/30"
                />
              </div>
              
              {submitted ? (
                <div className="bg-[#C97654]/20 border border-[#C97654] text-[#E8DCC4] px-6 py-4 rounded-md text-center" data-testid="enquiry-success">
                  <p className="font-medium">Thank you for your enquiry!</p>
                  <p className="text-sm text-[#E8DCC4]/70 mt-1">We'll be in touch shortly.</p>
                </div>
              ) : (
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#B85C38] hover:bg-[#C97654] text-white py-4 rounded-md transition-all duration-300 hover:scale-105 disabled:opacity-70"
                  data-testid="send-enquiry-btn"
                >
                  {submitting ? 'Sending...' : 'Send Enquiry'}
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ onTermsClick }) => (
  <footer className="bg-black pt-16 pb-10 border-t border-[#FBF6EC]/15" style={{fontFamily: "Georgia, 'Times New Roman', serif"}}>
    <div className="max-w-6xl mx-auto px-6 lg:px-10">
      {/* Top row: brand mark + tagline */}
      <div className="grid md:grid-cols-[1fr_auto] gap-8 items-start mb-10">
        <div>
          <p className="font-sans text-[11px] tracking-[0.38em] uppercase font-semibold text-[#C97654] mb-3">
            Passion Pilates · Chadstone
          </p>
          <h3 className="text-[#FBF6EC] text-[34px] md:text-[44px] leading-[1] font-normal tracking-[-1px]">
            Sweat · sculpt · <em className="italic text-[#C97654]">slay.</em>
          </h3>
        </div>
        <div className="flex items-center gap-3 md:justify-end">
          <PilatesLogo className="w-10 h-10" />
        </div>
      </div>

      {/* Hairline */}
      <div className="h-px bg-[#FBF6EC]/15 mb-8"></div>

      {/* Bottom: legal copy + links */}
      <div className="grid md:grid-cols-[2fr_1fr] gap-8 items-start">
        <div className="font-sans text-[12px] leading-[1.7] text-[#E8DCC4]/55">
          <p className="mb-1">© {new Date().getFullYear()} Passion Pilates Au · All rights reserved.</p>
          <p className="mb-1">Passion Pilates Au Pty Ltd trading as Passion Pilates Au</p>
          <p className="mb-2">ACN 694798939 · ABN 97694798939</p>
          <p>
            By using this website you agree to our{' '}
            <button onClick={onTermsClick} className="underline underline-offset-2 decoration-[#C97654]/60 hover:text-[#C97654] transition-colors" data-testid="footer-terms-link">
              Terms and Conditions
            </button>.
          </p>
        </div>

        <div className="flex md:flex-col md:items-end gap-3 md:gap-2 font-sans text-[11px] tracking-[0.25em] uppercase font-semibold">
          <a href="/pilates-near" className="text-[#E8DCC4]/70 hover:text-[#C97654] transition-colors" data-testid="footer-nearby-link">Suburbs</a>
          <a href="/faq" className="text-[#E8DCC4]/70 hover:text-[#C97654] transition-colors" data-testid="footer-faq-link">FAQ</a>
          <a href="/blog" className="text-[#E8DCC4]/70 hover:text-[#C97654] transition-colors" data-testid="footer-blog-link">Journal</a>
          <a href="#" className="text-[#E8DCC4]/70 hover:text-[#C97654] transition-colors">Privacy Policy</a>
          <button onClick={onTermsClick} className="text-[#E8DCC4]/70 hover:text-[#C97654] transition-colors text-left md:text-right" data-testid="footer-terms-btn">Terms</button>
          <a href="mailto:passion.chadstone@gmail.com" className="text-[#E8DCC4]/70 hover:text-[#C97654] transition-colors">Contact</a>
        </div>
      </div>
    </div>
  </footer>
);

// Launch Popup Wrapper to access BookingContext
const LaunchPopupWrapper = ({ onClose }) => {
  return <LaunchPopup onClose={onClose} openMindBody={openMindBodyBooking} />;
};

// Launch Popup Component
const LaunchPopup = ({ onClose, openMindBody }) => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await fetch(`${BACKEND_URL}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          name: email.split('@')[0], 
          email, 
          message: 'Newsletter signup via launch popup',
          source: 'popup_newsletter'
        }),
      });
      setSubscribed(true);
    } catch (err) {
      console.error('Signup error:', err);
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md p-4" style={{fontFamily: "Georgia, 'Times New Roman', serif"}}>
      <div className="relative max-w-md w-full max-h-[92vh] overflow-y-auto rounded-md shadow-2xl bg-[#EFD5C8] text-[#1a1a1a]">

        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close popup"
          data-testid="popup-close-btn"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-[#1a1a1a] text-[#E8DCC4] hover:bg-[#C97654] flex items-center justify-center transition-colors duration-300 z-10"
        >
          <X className="w-4 h-4" strokeWidth={2.5} />
        </button>

        {/* Editorial Hero */}
        <div className="px-7 pt-10 pb-2">
          <h1 className="font-sans font-black text-[#1a1a1a] leading-[0.9] tracking-[-2px] text-[52px] md:text-[64px] uppercase">
            Spring<br/>
            <span className="font-black text-[#8B2635]">into Action.</span>
          </h1>
          <div className="mt-4 w-16 h-[4px] bg-[#C97654]"></div>
        </div>

        {/* Spring Challenge — poster + minimal CTA */}
        <div className="mx-7 mt-6 bg-[#C97654] rounded-md overflow-hidden">
          <a
            href="https://instagram.com/passionpilates.au"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className="block group"
            data-testid="popup-spring-poster-link"
          >
            <img
              src="https://customer-assets-0z36b82j.emergentagent.net/job_170075d8-a168-40c8-9457-8f5d6f2e3d05/artifacts/w9qf5dyg_Cream%20and%20Peach%20Wedding%20Corflute%20Sign%20-%201.webp"
              alt="Spring Challenge — Hello Summer Bod! 3 classes a week over 10 weeks, starting 7 September"
              className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-500"
              loading="lazy"
            />
          </a>
          <div className="p-5">
            <p className="text-[11px] tracking-[0.28em] uppercase font-sans font-semibold text-[#1a1a1a] mb-2">Starts Sun 7 Sept</p>
            <a
              href="https://instagram.com/passionpilates.au"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              data-testid="popup-spring-instagram-btn"
              className="w-full inline-block text-center font-sans font-bold text-[13px] tracking-[0.2em] uppercase bg-[#1a1a1a] hover:bg-[#8B2635] text-[#E8DCC4] px-6 py-4 rounded-sm transition-colors duration-300"
            >
              Follow &amp; Enter →
            </a>
          </div>
        </div>

        {/* Or book a class */}
        <div className="px-7 pt-6 pb-2 text-center space-y-3">
          <a
            href="https://clients.mindbodyonline.com/classic/ws?studioid=5751621&stype=41"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            data-testid="popup-buy-pass-link"
            className="block font-sans text-[12px] tracking-[0.2em] uppercase font-semibold text-[#1a1a1a] border-b-2 border-[#C97654] pb-[2px] hover:text-[#C97654] transition-colors mx-auto w-fit"
          >
            buy a pass →
          </a>
          <button
            onClick={() => { onClose(); setTimeout(() => openMindBody(), 300); }}
            className="font-sans text-[12px] tracking-[0.2em] uppercase font-semibold text-[#1a1a1a] border-b-2 border-[#C97654] pb-[2px] hover:text-[#C97654] transition-colors"
          >
            or just book a class →
          </button>
        </div>

        {/* Newsletter Signup */}
        <div className="px-7 pb-8 pt-6 mt-3 border-t border-[#1a1a1a]/15">
          <p className="text-[11px] tracking-[0.3em] uppercase font-sans font-semibold text-[#8B2635] mb-3 text-center">
            Stay in the loop
          </p>

          {subscribed ? (
            <div data-testid="newsletter-success" className="bg-[#1a1a1a] text-[#E8DCC4] px-4 py-4 text-center rounded-sm">
              <p className="font-sans font-bold text-sm">Thanks for subscribing.</p>
              <p className="text-[12px] mt-1 opacity-75 font-sans">We'll keep you in the loop on new offers.</p>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="newsletter-email-input"
                className="flex-1 bg-transparent border-b-2 border-[#1a1a1a] focus:border-[#C97654] px-1 py-2 text-[#1a1a1a] placeholder-[#1a1a1a]/40 focus:outline-none font-sans text-sm"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="newsletter-submit-btn"
                className="bg-[#1a1a1a] hover:bg-[#C97654] text-[#E8DCC4] px-5 py-2 rounded-sm font-sans font-bold text-[12px] tracking-[0.25em] uppercase disabled:opacity-50 transition-colors"
              >
                {submitting ? '…' : 'Join'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Payment Success Modal
const PaymentSuccessModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/90 backdrop-blur-sm">
      <div className="relative bg-[#1a1a1a] border border-[#C97654] rounded-2xl p-8 md:p-12 max-w-lg mx-4 text-center">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-[#E8DCC4]/60 hover:text-[#C97654] transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
        
        <div className="mb-6">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-10 h-10 text-green-500" />
          </div>
        </div>
        
        <h2 className="text-3xl md:text-4xl font-light text-[#E8DCC4] mb-4">
          Payment Successful!
        </h2>
        <p className="text-xl text-[#C97654] mb-4">
          Welcome to Passion Pilates! 🎉
        </p>
        <p className="text-[#E8DCC4]/70 mb-6 font-light">
          A confirmation email with all your purchase details has been sent to your inbox. We can't wait to help you <span className="text-[#C97654] font-medium">sweat &gt; sculpt &gt; slay!</span>
        </p>
        <p className="text-[#E8DCC4]/60 text-sm mb-6">
          Our team will be in touch shortly to set up your account and book your first class.
        </p>
        <button 
          onClick={onClose}
          className="bg-[#C97654] hover:bg-[#B85C38] text-white px-8 py-3 rounded-md transition-all duration-300 font-medium"
        >
          Continue Exploring
        </button>
      </div>
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard = ({ onClose }) => {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, transactionsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/summary`),
        fetch(`${BACKEND_URL}/api/admin/transactions?limit=100`)
      ]);
      
      const summaryData = await summaryRes.json();
      const transactionsData = await transactionsRes.json();
      
      setSummary(summaryData);
      setTransactions(transactionsData.transactions || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredTransactions = filter === 'all' 
    ? transactions 
    : transactions.filter(t => t.payment_status === filter);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 z-[400] bg-[#0a0a0a] overflow-y-auto">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-light text-[#E8DCC4] mb-2">Sales Dashboard</h1>
            <p className="text-[#E8DCC4]/60 text-sm">Passion Pilates Au - Admin Panel</p>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded-lg text-[#E8DCC4] hover:border-[#C97654] transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button 
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 bg-[#C97654] rounded-lg text-white hover:bg-[#B85C38] transition-colors"
            >
              <X className="w-4 h-4" />
              Close
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#C97654] animate-spin" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-[#C97654]/20 rounded-lg">
                    <ShoppingBag className="w-6 h-6 text-[#C97654]" />
                  </div>
                  <div>
                    <p className="text-[#E8DCC4]/60 text-sm">Total Transactions</p>
                    <p className="text-2xl font-semibold text-[#E8DCC4]">{summary?.total_transactions || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-500/20 rounded-lg">
                    <Check className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-[#E8DCC4]/60 text-sm">Paid</p>
                    <p className="text-2xl font-semibold text-green-500">{summary?.paid_transactions || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-yellow-500/20 rounded-lg">
                    <Clock className="w-6 h-6 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-[#E8DCC4]/60 text-sm">Pending</p>
                    <p className="text-2xl font-semibold text-yellow-500">{summary?.pending_transactions || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#1a1a1a] border border-[#C97654] rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-[#C97654]/20 rounded-lg">
                    <DollarSign className="w-6 h-6 text-[#C97654]" />
                  </div>
                  <div>
                    <p className="text-[#E8DCC4]/60 text-sm">Total Revenue</p>
                    <p className="text-2xl font-semibold text-[#C97654]">${(summary?.total_revenue || 0).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Revenue by Package */}
            {summary?.revenue_by_package?.length > 0 && (
              <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 mb-8">
                <h2 className="text-xl font-medium text-[#E8DCC4] mb-4">Revenue by Package</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {summary.revenue_by_package.map((pkg, i) => (
                    <div key={i} className="bg-[#2a2a2a] rounded-lg p-4">
                      <p className="text-[#E8DCC4] font-medium mb-1">{pkg._id}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-[#E8DCC4]/60 text-sm">{pkg.count} sales</span>
                        <span className="text-[#C97654] font-semibold">${pkg.total.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Transactions Table */}
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl overflow-hidden">
              <div className="p-6 border-b border-[#2a2a2a] flex items-center justify-between">
                <h2 className="text-xl font-medium text-[#E8DCC4]">Recent Transactions</h2>
                <div className="flex gap-2">
                  {['all', 'paid', 'pending'].map((f) => (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className={`px-3 py-1 rounded-full text-sm capitalize transition-colors ${
                        filter === f 
                          ? 'bg-[#C97654] text-white' 
                          : 'bg-[#2a2a2a] text-[#E8DCC4]/70 hover:bg-[#3a3a3a]'
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#2a2a2a]">
                    <tr>
                      <th className="text-left px-6 py-3 text-[#E8DCC4]/60 text-sm font-medium">Date</th>
                      <th className="text-left px-6 py-3 text-[#E8DCC4]/60 text-sm font-medium">Package</th>
                      <th className="text-left px-6 py-3 text-[#E8DCC4]/60 text-sm font-medium">Type</th>
                      <th className="text-left px-6 py-3 text-[#E8DCC4]/60 text-sm font-medium">Amount</th>
                      <th className="text-left px-6 py-3 text-[#E8DCC4]/60 text-sm font-medium">Status</th>
                      <th className="text-left px-6 py-3 text-[#E8DCC4]/60 text-sm font-medium">Email Sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-8 text-center text-[#E8DCC4]/50">
                          No transactions found
                        </td>
                      </tr>
                    ) : (
                      filteredTransactions.map((tx, i) => (
                        <tr key={i} className="border-t border-[#2a2a2a] hover:bg-[#2a2a2a]/50">
                          <td className="px-6 py-4 text-[#E8DCC4]/80 text-sm">{formatDate(tx.created_at)}</td>
                          <td className="px-6 py-4 text-[#E8DCC4] text-sm font-medium">{tx.package_name}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded text-xs ${
                              tx.payment_type === 'recurring' 
                                ? 'bg-purple-500/20 text-purple-400' 
                                : 'bg-blue-500/20 text-blue-400'
                            }`}>
                              {tx.payment_type || 'one_time'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-[#C97654] font-medium">${tx.amount?.toFixed(2)} {tx.currency?.toUpperCase()}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded text-xs ${
                              tx.payment_status === 'paid' 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {tx.payment_status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {tx.email_sent ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <span className="text-[#E8DCC4]/40">-</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Quick Links */}
            <div className="mt-8 text-center">
              <p className="text-[#E8DCC4]/60 text-sm mb-4">For detailed reports and customer management:</p>
              <a 
                href="https://dashboard.stripe.com/payments" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#635BFF] text-white rounded-lg hover:bg-[#5851db] transition-colors"
              >
                Open Stripe Dashboard
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Main App
// AI Chatbot Widget
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'bot', text: 'Hi! I\'m the Passion Pilates assistant. How can I help you today?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => 'chat_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8));
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMsg, session_id: sessionId }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'bot', text: data.response || 'Sorry, I couldn\'t process that. Please try again.' }]);
    } catch {
      setMessages(prev => [...prev, { role: 'bot', text: 'Sorry, I\'m having trouble connecting. Please contact us directly at passion.chadstone@gmail.com' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Chat Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-[100] bg-[#B85C38] hover:bg-[#C97654] text-white px-4 py-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 flex items-center gap-2"
          data-testid="chatbot-toggle"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="text-xs font-medium">Chat Now</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-[100] w-[360px] max-h-[500px] bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="chatbot-window">
          {/* Header */}
          <div className="bg-[#B85C38] px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-white" />
              <span className="text-white font-medium text-sm">Passion Pilates</span>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white" data-testid="chatbot-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px] max-h-[360px]">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                  msg.role === 'user' 
                    ? 'bg-[#B85C38] text-white rounded-br-sm' 
                    : 'bg-[#2a2a2a] text-[#E8DCC4]/90 rounded-bl-sm'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#2a2a2a] text-[#E8DCC4]/60 px-3 py-2 rounded-xl rounded-bl-sm text-sm">
                  <span className="animate-pulse">Typing...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="border-t border-[#2a2a2a] p-3 flex gap-2 flex-shrink-0">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything..."
              className="flex-1 bg-[#2a2a2a] text-[#E8DCC4] text-sm px-3 py-2 rounded-lg border-none outline-none placeholder-[#E8DCC4]/40"
              data-testid="chatbot-input"
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="bg-[#B85C38] hover:bg-[#C97654] text-white p-2 rounded-lg transition-colors disabled:opacity-50"
              data-testid="chatbot-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

function MarketingApp() {
  const [showPopup, setShowPopup] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [showGlobalTerms, setShowGlobalTerms] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);

  // Delay showing the launch popup until the bold Inter font has finished
  // loading, so the headline never flashes with the fallback serif font.
  useEffect(() => {
    let cancelled = false;
    const reveal = () => { if (!cancelled) setShowPopup(true); };
    // Wait for the specific 900-weight face we use on the headline.
    if (typeof document !== "undefined" && document.fonts && document.fonts.load) {
      Promise.race([
        document.fonts.load('900 52px Inter').then(() => document.fonts.ready),
        new Promise((resolve) => setTimeout(resolve, 1500)), // hard cap so it always eventually shows
      ]).then(reveal);
    } else {
      setTimeout(reveal, 400);
    }
    return () => { cancelled = true; };
  }, []);

  // Scroll-reveal: fade each <section> in as it enters the viewport
  useEffect(() => {
    const sections = document.querySelectorAll("section[id]");
    sections.forEach((s) => s.classList.add("reveal-section"));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  // Check for payment success or admin page on load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    const admin = urlParams.get('admin');
    const terms = urlParams.get('terms');
    
    if (terms === 'true') {
      setShowGlobalTerms(true);
      setShowPopup(false);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (admin === 'true') {
      setShowAdmin(true);
      setShowPopup(false);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (payment === 'success' && sessionId) {
      setShowPaymentSuccess(true);
      setShowPopup(false);
      
      // Check payment status and trigger email
      fetch(`${BACKEND_URL}/api/checkout/status/${sessionId}`)
        .then(res => res.json())
        .then(data => {
          console.log('Payment status:', data);
        })
        .catch(err => console.error('Error checking payment status:', err));
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (payment === 'cancelled') {
      // Clean up URL on cancellation too
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Show admin dashboard if enabled
  if (showAdmin) {
    return <AdminDashboard onClose={() => setShowAdmin(false)} />;
  }

  return (
    <BookingProvider>
      <div className="min-h-screen bg-black">
        {showPaymentSuccess && <PaymentSuccessModal isOpen={showPaymentSuccess} onClose={() => setShowPaymentSuccess(false)} />}
        {showPopup && !showPaymentSuccess && <LaunchPopupWrapper onClose={() => setShowPopup(false)} />}
        <Navigation />
        <Hero />
        <About />
        <Classes />
        <OpeningSpecials />
        <Schedule />
        <Memberships />
        <Pricing />
        <FAQ />
        <Contact />
        <Footer onTermsClick={() => setShowGlobalTerms(true)} />
        <Chatbot />
        {showGlobalTerms && <TermsModal onClose={() => setShowGlobalTerms(false)} />}
      </div>
    </BookingProvider>
  );
}

// React Router import (kept at bottom to minimise edits to the existing monolith)
import { BrowserRouter, Routes, Route } from "react-router-dom";
import MemberApp from "./memberApp/MemberApp";
import BlogIndex from "./blog/BlogIndex";
import BlogPost from "./blog/BlogPost";
import NearbyIndex from "./seo/NearbyIndex";
import NearbySuburb from "./seo/NearbySuburb";
import FAQPage from "./seo/FAQPage";

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/app/*" element={<MemberApp />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/pilates-near" element={<NearbyIndex />} />
        <Route path="/pilates-near/:slug" element={<NearbySuburb />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="*" element={<MarketingApp />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
